import { type LocaleState } from "@/app/store";

import { type AddToCartSource } from "@/app/components/shared/recommended-products/add-to-cart-event";
import {
	type AddToCartEvent,
	type AnalyticsEvent,
	type ViewItemEvent,
	type ViewItemListEvent
} from "./analytics-events";
import { getCommonAnalytics, onAnalyticsReady } from "./common-analytics";

const VIEW_ITEM_LIST_NAME = "PDP";

const CURRENCY_BY_LOCALE: Record<LocaleState, string> = {
	"en-US": "USD",
	"en-CA": "CAD"
};

// Strings mirror the legacy Hybris PDP dataLayer so GA4 reporting stays
// continuous across the migration (verified against test Hybris). atc/cart
// surfaces are not on the PDP yet — those values are unverified against Hybris.
const PRODUCT_FINDING_METHOD_BY_SOURCE: Record<AddToCartSource, string> = {
	pdp: "PDP",
	"pdp-recommended": "product_recommendations_pdp",
	"atc-recommended": "product_recommendations_a2c",
	"cart-recommended": "product_recommendations_cart",
	"recently-viewed": "PDP"
};

const ITEM_LIST_NAME_BY_SOURCE: Record<AddToCartSource, string> = {
	pdp: "product detail page",
	"pdp-recommended": "PDP Product Recommendation",
	"atc-recommended": "A2C Product Recommendations",
	"cart-recommended": "Cart Product Recommendations",
	"recently-viewed": "Recently Viewed"
};

/**
 * Global, typed entry point for the analytics events consumer-web emits.
 * `track` is the single chokepoint: it SSR-guards, waits for common-analytics
 * to load, then routes the typed event to the matching common-analytics call.
 * Add a new `case` here when extending `AnalyticsEvent`.
 */
export const analyticsManager = {
	track(event: AnalyticsEvent): void {
		if (typeof window === "undefined") return;
		onAnalyticsReady(() => dispatch(event));
	}
};

function dispatch(event: AnalyticsEvent): void {
	switch (event.type) {
		case "add_to_cart":
			trackAddToCart(event);
			break;
		case "view_item":
			trackViewItem(event);
			break;
		case "view_item_list":
			trackViewItemList(event);
			break;
	}
}

function trackAddToCart(event: AddToCartEvent): void {
	const addToCart = getCommonAnalytics()?.events.addToCart;
	if (typeof addToCart !== "function") return;

	addToCart(
		CURRENCY_BY_LOCALE[event.locale],
		[{ id: event.sku, quantity: event.quantity, price: event.price }],
		ITEM_LIST_NAME_BY_SOURCE[event.source],
		PRODUCT_FINDING_METHOD_BY_SOURCE[event.source]
	);
}

function trackViewItem(event: ViewItemEvent): void {
	const viewItem = getCommonAnalytics()?.events.viewItem;
	if (typeof viewItem !== "function") return;

	Promise.resolve(viewItem(VIEW_ITEM_LIST_NAME, [event.sku])).catch(() => {});
}

function trackViewItemList(event: ViewItemListEvent): void {
	const viewItemList = getCommonAnalytics()?.events.viewItemList;
	if (typeof viewItemList !== "function" || event.skus.length === 0) return;

	Promise.resolve(viewItemList(event.itemListName, event.skus)).catch(
		() => {}
	);
}
